// GerenciamentoClientesPage.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  Pagination,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Select,
  MenuItem
} from '@mui/material';
import { Edit, ToggleOn, ToggleOff, Search, Person, Business, Sync, Add } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import AppBarComponent from '../../../components/AppBarComponent';

// Constantes para paginação
const DEFAULT_PAGE_SIZE = 10;
const PAGINATION_OPTIONS = [10, 25, 50];

// Componentes estilizados
const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

const StatusBadge = styled(Chip)(({ status, theme }) => ({
  backgroundColor: status ? theme.palette.success.main : theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  borderRadius: theme.shape.borderRadius,
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(4),
  '& .MuiOutlinedInput-root': {
    height: '56px',
    fontSize: '1rem',
    borderRadius: theme.shape.borderRadius,
    paddingLeft: theme.spacing(1),
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    transform: 'translate(44px, 18px) scale(1)',
    '&.Mui-focused': {
      transform: 'translate(14px, -9px) scale(0.75)',
      color: theme.palette.primary.main,
    },
    '&.MuiFormLabel-filled': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputAdornment-root': {
    marginRight: theme.spacing(1),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    borderColor: theme.palette.divider,
  },
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: theme.palette.action.hover,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  '& svg': {
    fontSize: '4rem',
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
}));

export default function GerenciamentoClientesPage() {
  const [clientes, setClientes] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isToggling, setIsToggling] = useState(false);

  const navigate = useNavigate();

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      console.error('REACT_APP_API_BASE_URL não está definido no arquivo .env');
      return endpoint;
    }
    const normalizedBase = baseUrl.replace(/\/+$/, '') + '/';
    return new URL(endpoint, normalizedBase).toString();
  };

  const fetchClientes = useCallback(async (page, search = '') => {
    setIsLoading(true);
    setError(null);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.token) {
        throw new Error('Usuário não autenticado');
      }
      
      const params = {
        page,
        size: pagination.pageSize,
        ...(search && { searchTerm: search.trim() })
      };

      const url = buildApiUrl('customers/list');
      const response = await axios.get(url, {
        params,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
      });
      
      if (!response.data?.data) {
        throw new Error('Estrutura de dados inválida na resposta');
      }

      setClientes(response.data.data);
      setPagination(prev => ({
        ...prev,
        currentPage: response.data.meta?.currentPage || 1,
        totalPages: response.data.meta?.totalPages || 1,
        totalItems: response.data.meta?.totalItems || 0
      }));
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setError(error.response?.data?.message || 
              error.message || 
              'Erro ao carregar clientes. Verifique sua conexão e tente novamente.');
      
      setSnackbar({
        open: true,
        message: 'Erro ao carregar clientes',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.pageSize]); 

  useEffect(() => {
    fetchClientes(pagination.currentPage, appliedSearchTerm);
  }, [pagination.currentPage, appliedSearchTerm, fetchClientes]);

  const handlePageChange = (_, page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({
      ...prev,
      pageSize: e.target.value,
      currentPage: 1
    }));
  };

  const handleEdit = (cliente) => {
    if (!cliente.status) {
      setSnackbar({
        open: true,
        message: 'Não é possível editar um cliente inativo',
        severity: 'warning',
      });
      return;
    }
    
    const clienteData = {
      ...cliente,
      phone: cliente.phone || '',
      observation: cliente.observation || '',
      address: {
        ...cliente.address,
        complement: cliente.address?.complement || '',
        neighborhood: cliente.address?.neighborhood || ''
      }
    };
    
    navigate(`/clients-update/${encodeURIComponent(cliente.email)}`, {
      state: { clienteData }
    });
  };

  const toggleClientStatus = async (cliente) => {
    if (isToggling) return;
    
    const newStatus = !cliente.status;
    setIsToggling(true);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Atualização otimista
      setClientes(prev => prev.map(c => 
        c.email === cliente.email ? { ...c, status: newStatus } : c
      ));
      
      const endpoint = newStatus ? 'enable' : 'disable';
      const url = buildApiUrl(`customers/${endpoint}/${encodeURIComponent(cliente.email)}`);
      
      await axios.patch(url, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
      });

      setSnackbar({
        open: true,
        message: `Cliente ${newStatus ? 'ativado' : 'desativado'} com sucesso!`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      // Reverte a mudança em caso de erro
      setClientes(prev => prev.map(c => 
        c.email === cliente.email ? { ...c, status: !newStatus } : c
      ));
      
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Erro ao ${newStatus ? 'ativar' : 'desativar'} cliente`,
        severity: 'error',
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatClientType = (type) => {
    return type === 'Individual' ? <Person color="primary" /> : <Business color="primary" />;
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.street || ''}, ${address.number || ''}${address.complement ? ` (${address.complement})` : ''}`;
  };

  return (
    <section>
      <AppBarComponent title="Gerenciamento de Clientes" />
      <Container elevation={0}>
        <SearchField
          fullWidth
          variant="outlined"
          label="Buscar clientes"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <EmptyState>
            <Typography color="error" variant="h6" gutterBottom>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => fetchClientes(pagination.currentPage, appliedSearchTerm)}
              startIcon={<Sync />}
            >
              Tentar novamente
            </Button>
          </EmptyState>
        ) : clientes.length === 0 ? (
          <EmptyState>
            <Person fontSize="inherit" />
            <Typography variant="h6" color="textSecondary">
              {appliedSearchTerm 
                ? 'Nenhum cliente encontrado com esse critério de busca.' 
                : 'Nenhum cliente cadastrado.'}
            </Typography>
            {!appliedSearchTerm && (
              <Button 
                variant="outlined" 
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => navigate('/clients/register')}
                startIcon={<Add />}
              >
                Adicionar primeiro cliente
              </Button>
            )}
          </EmptyState>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" color="textSecondary">
                Mostrando {clientes.length} de {pagination.totalItems} clientes
              </Typography>
              
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="textSecondary">
                  Itens por página:
                </Typography>
                <Select
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  {PAGINATION_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>CPF/CNPJ</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell width="50px">Tipo</TableCell>
                    <TableCell>Endereço</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>País</TableCell>
                    <TableCell width="120px">Status</TableCell>
                    <TableCell width="150px" align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientes.map((cliente) => (
                    <TableRow key={cliente.email} hover>
                      <TableCell>
                        <Typography fontWeight="500">
                          {cliente.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{cliente.identifier}</TableCell>
                      <TableCell>{cliente.email}</TableCell>
                      <TableCell>{cliente.phone || 'N/A'}</TableCell>
                      <TableCell>
                        <Tooltip title={cliente.clientType === 'Individual' ? 'Pessoa Física' : 'Pessoa Jurídica'}>
                          {formatClientType(cliente.clientType)}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {formatAddress(cliente.address)}
                        <Typography variant="caption" display="block" color="textSecondary">
                          {cliente.address?.neighborhood || 'N/A'}, {cliente.address?.city || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{cliente.address?.state || 'N/A'}</TableCell>
                      <TableCell>{cliente.address?.country || 'N/A'}</TableCell>
                      <TableCell>
                        <StatusBadge 
                          label={cliente.status ? 'Ativo' : 'Inativo'} 
                          status={cliente.status} 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title={cliente.status ? "Editar cliente" : "Cliente inativo - não pode ser editado"}>
                            <span>
                              <ActionButton
                                color="primary"
                                onClick={() => handleEdit(cliente)}
                                disabled={!cliente.status}
                              >
                                <Edit />
                              </ActionButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title={cliente.status ? "Desativar cliente" : "Ativar cliente"}>
                            <ActionButton
                              color={cliente.status ? "error" : "success"}
                              onClick={() => toggleClientStatus(cliente)}
                              disabled={isToggling}
                            >
                              {cliente.status ? <ToggleOff /> : <ToggleOn />}
                            </ActionButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </Box>

            <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Página {pagination.currentPage} de {pagination.totalPages}
              </Typography>
              
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                disabled={isLoading}
                showFirstButton
                showLastButton
                showPrevButton
                showNextButton
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '0.875rem',
                    minWidth: 32,
                    height: 32,
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                  }
                }}
              />
            </Box>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%' }}
            elevation={6}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </section>
  );
}